<template>
  <Content
    class="col-span-full"
    :title="$route.name"
    type="list"
    :pagination="{
      count: tableData.count,
      current: +$route.query.p,
      request: 'services/company-vehicles/',
    }"
    @tableData="tableData = $event"
    @loading="loading = $event"
  >
    <tableSearch
      @search="searchService"
      :tagValue="selectedTags.split(',')"
      :searchTag="true"
      :searchValue="searchValue"
      @selectedTags="searchTagInService"
      :loading="searchLoading"
      :placeholder="
        $t('SEARCH_FOR', {
          type: $t('search_for_license_and_fullname'),
        })
      "
    >
      <el-form-item :label="$t('START_DATE')" prop="dateRange.start">
        <date-picker
          v-model="dateRange.start"
          type="date"
          :locale="$i18n.locale"
          format="jYYYY-jMM-jDD"
          @change="handleDateChange"
          color="#151515"
          class="el-input__inner"
          clearable
        />
      </el-form-item>
      <el-form-item :label="$t('END_DATE')" prop="dateRange.end">
        <date-picker
          v-model="dateRange.end"
          type="date"
          :locale="$i18n.locale"
          format="jYYYY-jMM-jDD"
          @change="handleDateChange"
          color="#151515"
          class="el-input__inner"
          clearable
        />
      </el-form-item>
    </tableSearch>

    <div v-if="tableData.results" class="flex justify-end mb-4">
      <el-button
        :loading="isDownloadLoading"
        type="info"
        size="small"
        round
        icon="el-icon-download"
        @click="downloadReport()"
      >
        {{ $t('DOWNLOAD_REPORT') }}
      </el-button>
    </div>

    <el-table
      :empty-text="$t('NO_DATA')"
      :data="tableData.results"
      v-loading="loading"
      :stripe="true"
      style="width: 100%"
    >
      <el-table-column
        sortable
        type="index"
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('INDEX')"
        width="60"
      >
        <template slot-scope="scope">
          <div :data-title="$t('INDEX')">
            {{ scope.$index + 1 }}
          </div>
        </template>
      </el-table-column>
      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('VEHICLE_TITLE')"
        width="150"
      >
        <template slot-scope="scope">
          <LicensePlate
            :vehicle="scope.row.company_vehicle.vehicle"
            :license_plate="scope.row.company_vehicle.license_plate"
            :chars="alphaList"
          ></LicensePlate>
        </template>
      </el-table-column>

      <el-table-column
        prop="service.fa_title"
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('SERVICE')"
        width="160"
      >
        <template slot-scope="scope">
          <div :data-title="$t('SERVICE')" class="flex">
            {{
              $i18n.locale == 'fa'
                ? scope.row.service.fa_title
                : scope.row.service.en_title
            }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        sortable
        :sort-method="sortprice"
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('PRICE')"
        width="140"
      >
        <template slot-scope="scope">
          <div :data-title="$t('PRICE')" class="flex">
            <span
              v-html="$options.filters.numberSeperator(scope.row.price)"
            ></span>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :align="$i18n.locale == 'fa' ? 'right' : 'left'"
        :label="$t('DATE')"
        width="120"
      >
        <template slot-scope="scope">
          <div :data-title="$t('DATE')">
            {{ scope.row.date | getPersianDate }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        align="center"
        :label="$t('DISTANCE_TRAVEL')"
        width="120"
      >
        <template slot-scope="scope">
          <div
            :data-title="$t('DISTANCE_TRAVEL')"
            v-html="
              $options.filters.numberSeperator1(scope.row.odometer / 1000)
            "
          ></div>
        </template>
      </el-table-column>

      <el-table-column label="" width="170">
        <template slot-scope="scope">
          <div class="fixed-column">
            <tableFeatures
              :description="scope.row.description"
              :router="{
                name: 'EDIT_SERVICE',
                id: scope.row.id,
              }"
              @remove="remove($event)"
              :title="$t('VEHICLE_SERVICE')"
              @open="open($event)"
              @view="view($event, 'VIEW_SERVICE')"
              :view="true"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>
  </Content>
</template>

<script>
import { serviceList, removeService, downloadServiceList } from '@/api/services'
import tableSearch from '@/components/elements/tableSearch'
import tableFeatures from '@/components/elements/tableFeatures'
import { licenseChars } from '@/api/global'
import LicensePlate from '@/components/cars/LicensePlate'
import { open } from '@/mixins/openDesc'
import { view } from '@/mixins/viewPage'
import { jalaliToGregorian } from '@/mixins/jalaliToGregorian'

export default {
  mixins: [open, view, jalaliToGregorian],
  data() {
    return {
      isDownloadLoading: false,
      tableData: {
        results: [],
      },
      search: '',
      searchValue: '',
      selectedTags: '',
      searchLoading: false,
      loading: true,
      alphaList: [
        {
          code: '',
          char: '',
          id: '',
        },
      ],
      dateRange: {
        start: null,
        end: null,
      },
    }
  },

  methods: {
    downloadReport() {
      this.isDownloadLoading = true
      const searchParams = {
        search: this.searchValue,
        company_vehicle__company_vehicle_tags__id__in:
          this.selectedTags || null,
      }

      if (this.dateRange.start) {
        searchParams.date__gte = this.jalaliToGregorian(
          this.dateRange.start,
          this.$i18n.locale
        )
      }

      if (this.dateRange.end) {
        searchParams.date__lte = this.jalaliToGregorian(
          this.dateRange.end,
          this.$i18n.locale
        )
      }
      downloadServiceList
        .api(searchParams)
        .then((res) => {
          this.isDownloadLoading = false
          if (res) {
            var blob = new Blob([res], { type: 'application/octet-stream' })

            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.style.display = 'none'
            a.href = url
            a.download = `services-CARPO.xlsx`
            document.body.appendChild(a)
            a.click()

            window.URL.revokeObjectURL(url)
          }
        })
        .catch((error) => {
          console.error('Download Error:', error)
        })
    },
    handleDateChange() {
      this.fetchData()
    },

    searchService(val) {
      this.searchValue = val
      this.fetchData()
    },

    searchTagInService(val) {
      this.selectedTags = val.join(',')
      this.fetchData()
    },

    fetchData() {
      this.searchLoading = true
      this.loading = true

      const searchParams = {
        search: this.searchValue,
        company_vehicle__company_vehicle_tags__id__in:
          this.selectedTags || null,
      }

      if (this.dateRange.start) {
        searchParams.date__gte = this.jalaliToGregorian(
          this.dateRange.start,
          this.$i18n.locale
        )
      }

      if (this.dateRange.end) {
        searchParams.date__lte = this.jalaliToGregorian(
          this.dateRange.end,
          this.$i18n.locale
        )
      }

      this.$router.replace({
        query: {
          ...this.$route.query,
          search: this.searchValue,
          company_vehicle__company_vehicle_tags__id__in: this.selectedTags,
          date__gte: this.dateRange.start ? this.dateRange.start : undefined,
          date__lte: this.dateRange.end ? this.dateRange.end : undefined,
        },
      })

      serviceList.api(searchParams).then((res) => {
        if (res) {
          this.tableData = res
        }
        this.loading = false
        this.searchLoading = false
      })
    },

    remove(id) {
      removeService.api(id).then((res) => {
        if (res) {
          this.$notify({
            title: this.$i18n.t('SOMETHING_DELETED', {
              type: this.$i18n.t('VEHICLE_SERVICE'),
            }),
            duration: 4000,
            customClass: 'info_msg',
          })
          this.tableData.results = this.tableData.results.filter(
            (table) => table.id !== id
          )
        }
      })
    },
    sortprice(a, b) {
      if (a.price < b.price) return -1
      if (a.price > b.price) return 1
      return 0
    },
    loadInitialData() {
      const {
        search,
        company_vehicle__company_vehicle_tags__id__in,
        date__gte,
        date__lte,
      } = this.$route.query

      this.searchValue = search || ''
      this.selectedTags = company_vehicle__company_vehicle_tags__id__in || ''
      this.dateRange.start = date__gte || null
      this.dateRange.end = date__lte || null

      this.fetchData()
    },
  },
  mounted() {
    this.loadInitialData()
    licenseChars.api().then((res) => {
      this.alphaList = res
    })
  },
  components: {
    tableSearch,
    tableFeatures,
    LicensePlate,
  },
}
</script>
